import type { FunctionComponent } from 'react'
import React, { useContext } from 'react'

import classnames from 'classnames'

import { SortingAscendingIcon } from '../../../Icons/Miscellaneous/SortingAscending'
import { SortingDefaultIcon } from '../../../Icons/Miscellaneous/SortingDefault'
import { SortingDescendingIcon } from '../../../Icons/Miscellaneous/SortingDescending'
import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import { Context as TableContext } from '../../Table.context'
import { sizeClassMap } from '../Cell/Cell.utils'
import styles from './Header.module.scss'

export const Header: FunctionComponent<Props> = ({
  index = 0,
  colSpan = 1,
  rowSpan = 1,
  sortDirection = 'default',
  children,
  onSort,
  nonSortStyles,
}) => {
  const tableContextValue = useContext(TableContext)
  const SortingIcon = IconsMapper[sortDirection]

  const ariaSort: AriaSortType =
    sortDirection === 'ascending' || sortDirection === 'descending'
      ? sortDirection
      : undefined

  return (
    <th
      {...(onSort && { 'aria-sort': ariaSort })}
      className={classnames(
        styles.header,
        onSort && styles.headerWithHover,
        tableContextValue.sizing &&
          sizeClassMap.get(tableContextValue.sizing[index])
      )}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope="col"
    >
      {onSort ? (
        <button
          onClick={onSort}
          className={styles.headerWithButton}
          type="button"
        >
          <span
            className={classnames(
              styles.headerButtonText,
              'sb-link-animation-wrapper',
              'sb-text-small-print'
            )}
          >
            {children}
          </span>
          <SortingIcon />
        </button>
      ) : (
        <TypographyV2
          tag="span"
          textStyle="sb-text-body-small-regular"
          style={nonSortStyles}
        >
          {children}
        </TypographyV2>
      )}
    </th>
  )
}

///////// IMPLEMENTATION /////////

const IconsMapper = {
  default: SortingDefaultIcon,
  ascending: SortingAscendingIcon,
  descending: SortingDescendingIcon,
}

type SortDirectionType = 'default' | 'ascending' | 'descending'
type AriaSortType = Exclude<SortDirectionType, 'default'> | 'none' | undefined

type Props = {
  /**
   * Optional index number
   */
  index?: number
  /**
   * Optional number for how many columns to span
   */
  colSpan?: number
  /**
   * Optional number for how many rows to span
   */
  rowSpan?: number
  /**
   * Optional Sort callback - triggers sorting UI
   */
  onSort?: () => void
  /**
   * Optional Sort icon state when sort is enabled - defaults to default
   */
  sortDirection?: SortDirectionType
  /**
   * Optional style overrides for non-sort columns
   */
  nonSortStyles?: React.CSSProperties
  /**
   * Children to render
   */
  children: React.ReactNode
}
